import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { Button } from '@sentinel/ui';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

const styles = stylex.create({
  card: {
    position: 'relative',
    overflow: 'hidden',
    paddingTop: '1.25rem',
    paddingBottom: '1.25rem',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    borderRadius: '0.75rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: `color-mix(in oklab, ${sentinel.textWarning} 32%, transparent)`,
    backgroundColor: sentinel.surfaceElevated,
    backgroundImage:
      `linear-gradient(180deg, color-mix(in oklab, ${sentinel.textWarning} 8%, transparent), transparent 80%)`,
    boxShadow: sentinel.shadowMedium,
    display: 'grid',
    gap: '0.875rem',
  },
  accentBar: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '3px',
    backgroundColor: sentinel.textWarning,
  },
  eyebrow: {
    fontSize: '0.6875rem',
    letterSpacing: '0.24em',
    textTransform: 'uppercase',
    color: sentinel.textWarning,
    margin: 0,
  },
  title: { margin: 0, fontWeight: 600, fontSize: '1.0625rem' },
  subtitle: {
    margin: 0,
    color: sentinel.textMuted,
    fontSize: '0.8125rem',
  },
  secretBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    paddingTop: '0.875rem',
    paddingBottom: '0.875rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    borderRadius: '0.5rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderEmphasis,
    backgroundColor: sentinel.surfaceSunken,
    fontFamily: sentinel.fontMono,
    fontSize: '0.875rem',
    wordBreak: 'break-all',
  },
  secret: { flex: 1, color: sentinel.textBody },
  ackRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.8125rem',
    color: sentinel.textMuted,
  },
});

export interface RevealCardProps {
  /** Cleartext secret — shown once. */
  secret: string;
  /** Optional human-readable name of the key (for the title). */
  label: string | null;
  /** Called once the user acknowledges they've stored the secret. */
  onDismiss: () => void;
}

export function RevealCard({ secret, label, onDismiss }: RevealCardProps) {
  const { t } = useTranslation('credentials');
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard rejection is silent — the value is still selectable.
    }
  }

  return (
    <section
      {...stylex.props(styles.card)}
      role="status"
      aria-live="polite"
      data-testid="api-key-reveal"
    >
      <span {...stylex.props(styles.accentBar)} aria-hidden="true" />
      <div>
        <p {...stylex.props(styles.eyebrow)}>{t('reveal_eyebrow')}</p>
        <h3 {...stylex.props(styles.title)}>
          {label ? t('reveal_title_with_label', { label }) : t('reveal_title')}
        </h3>
        <p {...stylex.props(styles.subtitle)}>{t('reveal_subtitle')}</p>
      </div>
      <div {...stylex.props(styles.secretBox)}>
        <code {...stylex.props(styles.secret)} data-testid="api-key-reveal-secret">
          {secret}
        </code>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleCopy}
          data-testid="api-key-reveal-copy"
        >
          {copied ? t('reveal_copied') : t('reveal_copy')}
        </Button>
      </div>
      <label {...stylex.props(styles.ackRow)}>
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          data-testid="api-key-reveal-ack"
        />
        {t('reveal_ack')}
      </label>
      <div>
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={!acknowledged}
          onClick={onDismiss}
          data-testid="api-key-reveal-dismiss"
        >
          {t('reveal_dismiss')}
        </Button>
      </div>
    </section>
  );
}
